import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DocumentsTab = ({ onUploadDocument }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [dragActive, setDragActive] = useState(false);

  const documents = [
    {
      id: "DOC-2025-001",
      name: "Medical History Summary",
      type: "Medical Record",
      format: "PDF",
      size: "2.4 MB",
      uploadedBy: "Dr. Sarah Johnson",
      uploadedDate: "2025-09-20",
      lastModified: "2025-09-20",
      category: "Medical Records",
      tags: ["History", "Summary", "Comprehensive"],
      fhirCompliant: true,
      encrypted: true,
      shared: false,
      description: "Comprehensive medical history summary including past diagnoses, surgeries, and treatments.",
      url: "/documents/medical-history-summary.pdf"
    },
    {
      id: "DOC-2025-002",
      name: "Chest X-Ray Report",
      type: "Imaging Report",
      format: "PDF",
      size: "1.8 MB",
      uploadedBy: "Dr. Michael Chen",
      uploadedDate: "2025-09-18",
      lastModified: "2025-09-18",
      category: "Imaging",
      tags: ["X-Ray", "Chest", "Radiology"],
      fhirCompliant: true,
      encrypted: true,
      shared: true,
      description: "Chest X-ray imaging report with radiologist interpretation and findings.",
      url: "/documents/chest-xray-report.pdf"
    },
    {
      id: "DOC-2025-003",
      name: "Insurance Card Scan",
      type: "Insurance Document",
      format: "PNG",
      size: "856 KB",
      uploadedBy: "Patient Portal",
      uploadedDate: "2025-09-15",
      lastModified: "2025-09-15",
      category: "Insurance",
      tags: ["Insurance", "Card", "Coverage"],
      fhirCompliant: false,
      encrypted: true,
      shared: false,
      description: "Scanned copy of current health insurance card for verification purposes.",
      url: "/documents/insurance-card.png"
    },
    {
      id: "DOC-2025-004",
      name: "Allergy Test Results",
      type: "Lab Report",
      format: "PDF",
      size: "3.2 MB",
      uploadedBy: "Dr. Emily Rodriguez",
      uploadedDate: "2025-09-12",
      lastModified: "2025-09-12",
      category: "Lab Results",
      tags: ["Allergy", "Test", "Results"],
      fhirCompliant: true,
      encrypted: true,
      shared: true,
      description: "Comprehensive allergy panel test results with detailed allergen sensitivity levels.",
      url: "/documents/allergy-test-results.pdf"
    },
    {
      id: "DOC-2025-005",
      name: "Medication List",
      type: "Medication Record",
      format: "JSON",
      size: "45 KB",
      uploadedBy: "System Generated",
      uploadedDate: "2025-09-10",
      lastModified: "2025-09-20",
      category: "Medications",
      tags: ["Medications", "Current", "FHIR"],
      fhirCompliant: true,
      encrypted: true,
      shared: false,
      description: "FHIR-compliant current medication list with dosages and prescribing information.",
      url: "/documents/medication-list.json"
    },
    {
      id: "DOC-2025-006",
      name: "Consent Form - Data Sharing",
      type: "Legal Document",
      format: "PDF",
      size: "1.1 MB",
      uploadedBy: "Patient Portal",
      uploadedDate: "2025-09-08",
      lastModified: "2025-09-08",
      category: "Legal",
      tags: ["Consent", "Privacy", "Legal"],
      fhirCompliant: false,
      encrypted: true,
      shared: false,
      description: "Signed consent form for health data sharing and privacy preferences.",
      url: "/documents/consent-form.pdf"
    }
  ];

  const getTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'medical record': return 'FileText';
      case 'imaging report': return 'Image';
      case 'lab report': return 'FlaskConical';
      case 'insurance document': return 'CreditCard';
      case 'medication record': return 'Pill';
      case 'legal document': return 'Scale';
      default: return 'File';
    }
  };

  const getFormatColor = (format) => {
    switch (format?.toLowerCase()) {
      case 'pdf': return 'bg-error text-error-foreground';
      case 'json': return 'bg-primary text-primary-foreground';
      case 'png': case 'jpg': case 'jpeg': return 'bg-success text-success-foreground';
      case 'docx': return 'bg-primary text-primary-foreground';
      default: return 'bg-secondary text-secondary-foreground';
    }
  };

  const filteredDocuments = documents?.filter(document => {
    const matchesSearch = document?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         document?.type?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         document?.tags?.some(tag => tag?.toLowerCase()?.includes(searchTerm?.toLowerCase()));
    
    const matchesFilter = filterType === 'all' || document?.category?.toLowerCase()?.includes(filterType?.toLowerCase());
    
    return matchesSearch && matchesFilter;
  });

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === "dragenter" || e?.type === "dragover") {
      setDragActive(true);
    } else if (e?.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    
    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      // Handle file upload
      console.log('Files dropped:', e?.dataTransfer?.files);
      onUploadDocument(e?.dataTransfer?.files);
    }
  };

  const handleFileSelect = (e) => {
    if (e?.target?.files && e?.target?.files?.[0]) {
      onUploadDocument(e?.target?.files);
    }
  };

  return (
    <div className="space-y-6">
      {/* Search and Filter Controls */}
      <div className="bg-card border border-border rounded-lg p-4 shadow-clinical">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search documents by name, type, or tags..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 bg-input border border-border rounded-md text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e?.target?.value)}
              className="px-4 py-2 bg-input border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="all">All Categories</option>
              <option value="medical">Medical Records</option>
              <option value="imaging">Imaging</option>
              <option value="lab">Lab Results</option>
              <option value="insurance">Insurance</option>
              <option value="medications">Medications</option>
              <option value="legal">Legal</option>
            </select>
            
            <Button variant="default" iconName="Upload" iconPosition="left" onClick={onUploadDocument}>
              Upload Document
            </Button>
          </div>
        </div>
      </div>
      {/* Upload Area */}
      <div
        className={`bg-card border-2 border-dashed rounded-lg p-8 text-center transition-clinical ${
          dragActive ? 'border-primary bg-primary/5' : 'border-border'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <Icon name="Upload" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">Upload Documents</h3>
        <p className="text-muted-foreground mb-4">
          Drag and drop files here, or click to select files
        </p>
        <input
          type="file"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          id="file-upload"
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.json"
        />
        <label htmlFor="file-upload">
          <Button variant="outline" iconName="FolderOpen" iconPosition="left">
            Select Files
          </Button>
        </label>
        <p className="text-xs text-muted-foreground mt-2">
          Supported formats: PDF, DOC, DOCX, JPG, PNG, JSON (Max 10MB per file)
        </p>
      </div>
      {/* Documents List */}
      <div className="space-y-4">
        {filteredDocuments?.map((document) => (
          <div key={document?.id} className="bg-card border border-border rounded-lg shadow-clinical">
            <div className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-muted/30 rounded-lg flex items-center justify-center">
                    <Icon name={getTypeIcon(document?.type)} size={20} className="text-muted-foreground" />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-foreground">{document?.name}</h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getFormatColor(document?.format)}`}>
                        {document?.format}
                      </span>
                      {document?.fhirCompliant && (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-accent text-accent-foreground">
                          FHIR
                        </span>
                      )}
                      {document?.encrypted && (
                        <Icon name="Lock" size={14} className="text-success" title="Encrypted" />
                      )}
                      {document?.shared && (
                        <Icon name="Share" size={14} className="text-primary" title="Shared" />
                      )}
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-2">
                      <span className="flex items-center gap-1">
                        <Icon name="FolderOpen" size={14} />
                        {document?.category}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="HardDrive" size={14} />
                        {document?.size}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="User" size={14} />
                        {document?.uploadedBy}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="Calendar" size={14} />
                        {new Date(document.uploadedDate)?.toLocaleDateString()}
                      </span>
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-3">{document?.description}</p>
                    
                    <div className="flex flex-wrap gap-1">
                      {document?.tags?.map((tag, index) => (
                        <span key={index} className="px-2 py-1 text-xs bg-muted text-muted-foreground rounded-md">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" iconName="Eye">
                    View
                  </Button>
                  <Button variant="outline" size="sm" iconName="Download">
                    Download
                  </Button>
                  <Button variant="outline" size="sm" iconName="Share">
                    Share
                  </Button>
                  <Button variant="ghost" size="sm" iconName="MoreVertical">
                    More
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {filteredDocuments?.length === 0 && (
        <div className="bg-card border border-border rounded-lg p-12 text-center shadow-clinical">
          <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No documents found</h3>
          <p className="text-muted-foreground mb-6">No documents match your current search criteria.</p>
          <Button variant="default" iconName="Upload" iconPosition="left" onClick={onUploadDocument}>
            Upload First Document
          </Button>
        </div>
      )}
    </div>
  );
};

export default DocumentsTab;